import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import type { RowData, Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { Decimal } from 'decimal.js';
import type { EdiColumn, EdiTableFeatures } from '@/libs/table';
import { MAX_DATE_INPUT } from '@/utils/date';

export interface FilterColumnMeta {
  filterVariant?: 'text' | 'range' | 'dateRange';
  /** 絞り込み欄の見出し */
  label?: string;
  placeholder?: string;
}

const isEmptyRangeValue = (value: unknown): boolean => value === '' || value === null || value === undefined;

const inputClassName = 'h-10';

/**
 * 数値の min/max で絞り込む。値は `[最小, 最大]` で保持する。
 * 両端が未入力になったらエントリごと削除して、絞り込み中の判定が残らないようにする。
 */
const RangeFilter = <T extends RowData>({ column, label }: { column: EdiColumn<T>; label: string }) => {
  const currentValue = column.getFilterValue() as (number | undefined)[] | undefined;

  // 入力値は文字列で来るので数値に変換する
  const toNumber = (value: string) => (value.length > 0 ? new Decimal(value).toNumber() : undefined);

  const update = (index: 0 | 1, value: string) => {
    const parsed = toNumber(value);
    column.setFilterValue((old: (number | undefined)[] | undefined) => {
      const next: (number | undefined)[] = index === 0 ? [parsed, old?.[1]] : [old?.[0], parsed];
      return isEmptyRangeValue(next[0]) && isEmptyRangeValue(next[1]) ? undefined : next;
    });
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode="decimal"
        value={currentValue?.[0] ?? ''}
        onChange={(e) => update(0, e.target.value)}
        className={inputClassName}
        placeholder="最小"
        aria-label={`${label}（最小）`}
      />
      <span className="text-neutral-600">〜</span>
      <input
        type="number"
        inputMode="decimal"
        value={currentValue?.[1] ?? ''}
        onChange={(e) => update(1, e.target.value)}
        className={inputClassName}
        placeholder="最大"
        aria-label={`${label}（最大）`}
      />
    </div>
  );
};

/**
 * 期間で絞り込む。値は `[開始日, 終了日]` (yyyy-MM-dd) で保持する。
 * 両端が空になったらエントリごと削除して、絞り込み中の判定が残らないようにする。
 */
const DateRangeFilter = <T extends RowData>({ column, label }: { column: EdiColumn<T>; label: string }) => {
  const currentValue = column.getFilterValue() as string[] | undefined;
  const startDate = Array.isArray(currentValue) ? (currentValue[0] ?? '') : '';
  const endDate = Array.isArray(currentValue) ? (currentValue[1] ?? '') : '';

  const update = (nextStart: string, nextEnd: string) => {
    if (nextStart === '' && nextEnd === '') {
      column.setFilterValue(undefined);
      return;
    }
    // 新しい配列にして変更を確実に検知させる
    column.setFilterValue([nextStart, nextEnd]);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        max={MAX_DATE_INPUT}
        value={startDate}
        onChange={(e) => update(e.target.value, endDate)}
        className={inputClassName}
        aria-label={`${label}（開始日）`}
      />
      <span className="text-neutral-600">〜</span>
      <input
        type="date"
        max={MAX_DATE_INPUT}
        value={endDate}
        onChange={(e) => update(startDate, e.target.value)}
        className={inputClassName}
        aria-label={`${label}（終了日）`}
      />
    </div>
  );
};

interface FilterPanelProps<T extends RowData = RowData> {
  table: Table<EdiTableFeatures, T>;
  /** 一覧上部の検索欄など、別の場所で入力させる列 */
  excludeColumnIds?: readonly string[];
}

/**
 * 列定義の meta.filterVariant を元に、表示中の列の絞り込み欄を並べる。
 */
export const FilterPanel = <T extends RowData = RowData>({
  table,
  excludeColumnIds = [],
}: FilterPanelProps<T>) => {
  const filterColumns = table.getVisibleLeafColumns().filter((column) => {
    const meta = column.columnDef.meta as FilterColumnMeta | undefined;
    return meta?.filterVariant && !excludeColumnIds.includes(column.id);
  });

  return (
    <div className="grid gap-x-6 gap-y-3 border border-neutral-200 bg-neutral-50 p-4 md:grid-cols-2 lg:grid-cols-3">
      {filterColumns.map((column) => {
        const meta = column.columnDef.meta as FilterColumnMeta;
        const label = meta.label ?? column.id;
        return (
          <div key={column.id} className="flex flex-col gap-1">
            {meta.filterVariant === 'text' ? (
              <label className="flex flex-col gap-1 text-sm">
                {label}
                <input
                  type="text"
                  value={(column.getFilterValue() as string) ?? ''}
                  onChange={(e) => column.setFilterValue(e.target.value)}
                  className={inputClassName}
                  placeholder={meta.placeholder}
                />
              </label>
            ) : (
              <>
                <span className="text-sm">{label}</span>
                {meta.filterVariant === 'range' && <RangeFilter column={column} label={label} />}
                {meta.filterVariant === 'dateRange' && <DateRangeFilter column={column} label={label} />}
              </>
            )}
          </div>
        );
      })}
      <div className="flex items-end md:col-span-full">
        <button
          type="button"
          onClick={() => table.resetColumnFilters()}
          className="h-10 cursor-pointer text-sm text-sky-800 underline hover:opacity-60"
        >
          条件をクリア
        </button>
      </div>
    </div>
  );
};

interface FilterToggleButtonProps {
  open: boolean;
  onToggle: () => void;
  activeCount: number;
}

export const FilterToggleButton = ({ open, onToggle, activeCount }: FilterToggleButtonProps) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className={clsx(
      'flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded border px-3 text-sm duration-300',
      open || activeCount > 0
        ? 'border-sky-800 text-sky-800'
        : 'border-neutral-300 bg-white text-zinc-800 hover:bg-neutral-100',
    )}
  >
    <AdjustmentsHorizontalIcon className="size-5" />
    絞り込み
    {activeCount > 0 && (
      <span className="min-w-5 bg-sky-800 px-1 text-center text-sm leading-5 font-bold text-white">
        {activeCount}
      </span>
    )}
  </button>
);
